// 会话存储：localStorage 的读写与有效性判断，全站唯一入口。
//
// ⚠ 跨仓契约（改这里必须同步改对侧）
// 对侧是 bees/www 仓的 src/utils/loginUtils.ts —— 当前登录/注册页在那边（营销站承接
// 转化路径），它写入这三个 key，本仓只读不写（唯一例外是 Login-1.vue 那条备用登录路径，
// 见 auth.module.js 的 SET_AUTH）。两侧必须逐字一致的有四条：
//   1. key 名：auth_token / user_info / token_expiration
//   2. token_expiration 存的是**绝对 Unix 秒**，不是有效期时长
//   3. 有效 = token 非空 且 过期时间存在 且 当前时刻未到过期时刻
//   4. 登出清全部三个 key，不能只清 token
// 任一条两侧不一致，症状都是「登录成功后被弹回登录页」——两个应用对同一份会话给出
// 相反的解读，且各自的单元测试都是绿的（接缝没人守）。
//
// 注：token_expiration 的绝对时刻由**写入方**换算。后端 bees/api 返回的 result.expiration
// 是有效期**时长**（硬编码 86400 秒，见 app/api_1_0/resources/auth.py:46），不是时刻；
// 直接存会让所有过期判断恒真。
//
// TODO(过渡): 登录/注册后续回归本仓、www 退回纯营销站后，本文件将成为会话的唯一写入方，
// 届时上面的跨仓契约随之作废，只保留 key 名与语义即可。
const TOKEN_KEY = "auth_token";
const USER_INFO_KEY = "user_info";
const TOKEN_EXPIRATION_KEY = "token_expiration";

export const getToken = () => {
  return window.localStorage.getItem(TOKEN_KEY);
};

export const getUserInfo = () => {
  const raw = window.localStorage.getItem(USER_INFO_KEY);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
};

/**
 * 写入会话。仅供 Login-1.vue 那条备用登录路径使用（正常登录在 bees/www）。
 * @param {string} token 后端签发的 token
 * @param {object} user 后端返回的原始 user 对象（不要塞 console 专属的 UI 字段，
 *   如 photo —— 那是本仓的展示需要，贴在 vuex state 上即可，不属于跨应用契约）
 * @param {number} expiresInSeconds 后端返回的有效期**时长**（秒），本函数负责换算成绝对时刻
 */
export const saveSession = (token, user, expiresInSeconds) => {
  const expiresAt = Math.floor(Date.now() / 1000) + expiresInSeconds;
  window.localStorage.setItem(TOKEN_KEY, token);
  window.localStorage.setItem(USER_INFO_KEY, JSON.stringify(user));
  window.localStorage.setItem(TOKEN_EXPIRATION_KEY, String(expiresAt));
};

/**
 * 会话是否有效。**纯函数，不做清理** —— 它在 vuex state 初始化时被调用，
 * 那个时机做 side effect 会让 store 的构造过程带上不可见的状态变更。
 * 过期会话的清理由踢出流程走 PURGE_AUTH。
 * @returns {boolean}
 */
export const isSessionValid = () => {
  const token = window.localStorage.getItem(TOKEN_KEY);
  if (!token) {
    return false;
  }
  const expiresAt = window.localStorage.getItem(TOKEN_EXPIRATION_KEY);
  // 缺过期时间即视为无效：让「漏写 expiration」显式失败，而不是静默放行。
  if (!expiresAt) {
    return false;
  }
  return Date.now() / 1000 < Number(expiresAt);
};

export const destroySession = () => {
  window.localStorage.removeItem(TOKEN_KEY);
  window.localStorage.removeItem(USER_INFO_KEY);
  window.localStorage.removeItem(TOKEN_EXPIRATION_KEY);
};

export default {
  getToken,
  getUserInfo,
  saveSession,
  isSessionValid,
  destroySession,
};
